import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { HvEmptyState, ListValueProp } from "@hitachivantara/uikit-react-core";
import {
  HvTable,
  HvTableBody,
  HvTableCell,
  HvTableContainer,
  HvTableHead,
  HvTableHeader,
  HvTableRow,
  HvTableOptions,
  HvTableInstance,
} from "@hitachivantara/uikit-react-lab";
import { Ban } from "@hitachivantara/uikit-react-icons";
import { setId } from "lib/utils/ids";
import LoadingContainer from "components/layout/LoadingContainer";
import useStyles from "./styles";

export interface MapPropertiesDataRow extends Record<string, unknown> {
  id: string;
  incidentProperty: string;
  incidentPropertyType: string;
  entityProperty: ListValueProp[];
  entityPropertyType: string;
}

interface TableProps extends HvTableOptions<MapPropertiesDataRow> {
  id: string;
  instance: HvTableInstance<MapPropertiesDataRow>;
  disablePagination?: boolean;
  selectAllFiltered?: boolean;
  loading?: boolean;
  labels?: Record<string, string>;
}

export const DEFAULT_PAGE = 0;
export const DEFAULT_PAGE_SIZE = 10;

const MapPropertiesTable = ({
  id,
  instance,
  loading = false,
}: TableProps): JSX.Element => {
  const classes = useStyles();

  const { t } = useTranslation("common");

  const { getTableProps, getTableBodyProps, prepareRow, headers, rows } =
    instance;

  const EmptyRow = useCallback(
    () => (
      <HvTableRow>
        <HvTableCell
          className={classes.noData}
          // colSpan is missing in the TS definition
          {...{ colSpan: headers.length }}
        >
          <HvEmptyState
            message={t("simple_table.noDataMessage")}
            icon={<Ban role="presentation" />}
          />
        </HvTableCell>
      </HvTableRow>
    ),
    [classes.noData, headers.length, t]
  );
  return (
    <LoadingContainer hidden={!loading}>
      <HvTableContainer className={classes.tableContainer}>
        <HvTable id={id} {...getTableProps()} className={classes.table}>
          <HvTableHead className={classes.tableHead}>
            <HvTableRow className={clsx(classes.tableRow)}>
              {headers.map((col) => (
                <HvTableHeader
                  {...col.getHeaderProps()}
                  id={setId(id, col.id)}
                  className={clsx(classes.tableHeader, {
                    [classes.tableCellAction]: col.variant === "actions",
                  })}
                >
                  {col.render("Header")}
                </HvTableHeader>
              ))}
            </HvTableRow>
          </HvTableHead>
          <HvTableBody {...getTableBodyProps()} className={classes.tableBody}>
            {rows.length > 0 ? (
              rows.map((row) => {
                prepareRow(row);

                return (
                  <HvTableRow
                    className={clsx(classes.tableRow)}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => {
                      const cellProps = cell.getCellProps();

                      return (
                        <HvTableCell
                          {...cellProps}
                          className={clsx(classes.tableCell, {
                            [classes.tableCellAction]:
                              cell.column.variant === "actions",
                          })}
                          {...(cell.column.id === "updatedDate" && {
                            style: {
                              ...cellProps.style,
                              borderLeft: `solid 2px red`,
                              paddingLeft: 28,
                            },
                          })}
                        >
                          {cell.render("Cell")}
                        </HvTableCell>
                      );
                    })}
                  </HvTableRow>
                );
              })
            ) : (
              <EmptyRow />
            )}
          </HvTableBody>
        </HvTable>
      </HvTableContainer>
    </LoadingContainer>
  );
};

export default MapPropertiesTable;
